import { PAGE_BUILDER_ENDPOINTS } from './constants';
import { getSessionObject, httpMiddleware, showSuccessToast } from './Utils';
import { htmlPageErrorMessage } from './html-page-errors';

function currentPageId() {
  try {
    const metadata = getSessionObject('metaData');
    const data = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
    return Number(data?.pageId);
  } catch (_) { return 0; }
}

export default function registerHtmlPageZipTrait(editor) {
  const uploads = new Set();
  const pending = new Map();
  const fields = new Set();
  const panels = new Set();
  const display = (field, data) => {
    if (field.name === 'url') {
      const basePath = typeof data?.basePath === 'string' ? data.basePath : '';
      if (!basePath) {
        field.root.textContent = 'Unavailable';
      } else {
        field.root.textContent = basePath;
      }
      return;
    }
    let value = data?.[field.name];
    if (field.name === 'uploadError') value = htmlPageErrorMessage(value);
    if (field.name === 'updatedAt' && value) {
      const date = new Date(value);
      value = Number.isNaN(date.getTime()) ? 'Unavailable' : date.toLocaleString();
    }
    if (field.name === 'uploadedZipSize' && typeof value === 'number') value = `${(value / (1024 * 1024)).toFixed(2)} MB`;
    const missing = ['pendingVersion', 'uploadError'].includes(field.name) ? 'None' : 'Unavailable — reupload ZIP to record';
    field.root.textContent = value == null || value === '' ? missing : String(value);
  };
  const details = (pageId) => {
    if (!pending.has(pageId)) {
      const promise = httpMiddleware({
        url: `${PAGE_BUILDER_ENDPOINTS.HTML_PAGE_API}/${pageId}/metadata`, method: 'GET',
        withCredentials: true, rejectOnError: true,
      }).then((response) => {
        if (!response || response.status !== 200) throw new Error('Unable to load upload details.');
        return response.data;
      }).finally(() => { if (pending.get(pageId) === promise) pending.delete(pageId); });
      pending.set(pageId, promise);
    }
    return pending.get(pageId);
  };
  editor.TraitManager.addType('html-page-upload-info', {
    eventCapture: [],
    createInput({ trait }) {
      const root = document.createElement('div');
      root.style.cssText = 'padding:8px;overflow-wrap:anywhere;user-select:text;';
      root.setAttribute('aria-readonly', 'true');
      root.textContent = 'Loading…';
      // Trait inputs may be constructed before their panel is attached to the DOM.
      // Keep pending inputs, but bound references across repeated panel selections.
      if (fields.size >= 200) fields.delete(fields.values().next().value);
      const field = { root, name: trait.get('uploadField'), pageId: currentPageId() };
      fields.add(field);
      if (!Number.isSafeInteger(field.pageId) || field.pageId <= 0) root.textContent = 'Save or select an HTML page first.';
      else details(field.pageId).then((data) => {
        // A concurrent reupload may already have supplied fresher metadata.
        if (!field.refreshed) display(field, data);
      }).catch(() => { if (!field.refreshed) root.textContent = 'Unable to load upload details. Reselect the handler to retry.'; });
      return root;
    },
  });
  const poller = window.setInterval(() => {
    for (const panel of panels) {
      if (!panel.root.isConnected) { panels.delete(panel); continue; }
      panel.refresh().catch(() => {});
    }
  }, 20000);
  editor.on('destroy', () => { window.clearInterval(poller); fields.clear(); panels.clear(); pending.clear(); uploads.clear(); });
  editor.TraitManager.addType('html-page-zip-upload', {
    // Uploads are actions, not component attributes: never persist a File or execute its HTML.
    eventCapture: [],
    createInput() {
      const root = document.createElement('div');
      root.style.cssText = 'display:grid;gap:10px;padding:8px;';
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.zip,application/zip,application/x-zip-compressed';
      input.setAttribute('aria-label', 'Replacement HTML page ZIP');
      input.style.maxWidth = '100%';
      const help = document.createElement('small');
      help.textContent = 'Include an HTML file. If index.html is missing, another .html file is used as the entry page. Maximum ZIP: 50 MB. Reupload publishes immediately and keeps the page URL and layout.';
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = 'Reupload ZIP';
      button.disabled = true;
      const retryButton = document.createElement('button');
      retryButton.type = 'button'; retryButton.textContent = 'Retry failed upload'; retryButton.disabled = true;
      const refreshButton = document.createElement('button');
      refreshButton.type = 'button'; refreshButton.textContent = 'Refresh upload status';
      const status = document.createElement('div');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      root.append(input, help, button, retryButton, refreshButton, status);
      // Bind this view to its page so switching pages cannot upload to the wrong page.
      const pageId = currentPageId();
      const showError = (message) => { status.textContent = message; status.style.color = '#d32f2f'; };
      let busy = false;
      let uploadInfo = null;
      let statusGeneration = 0;
      const updateButtons = () => {
        const zip = input.files?.[0];
        const selected = zip && zip.name.toLowerCase().endsWith('.zip') && zip.size > 0 && zip.size <= 50 * 1024 * 1024;
        const active = uploadInfo?.uploadState === 'UPLOADING';
        button.disabled = busy || uploads.has(pageId) || !selected || !uploadInfo || active;
        retryButton.disabled = busy || uploads.has(pageId) || !selected || !uploadInfo?.retryAllowed;
      };
      const applyDetails = (data) => {
        uploadInfo = data;
        for (const field of fields) {
          if (!field.root.isConnected) continue;
          if (field.pageId === pageId) { field.refreshed = true; display(field, data); }
        }
        updateButtons();
      };
      const refresh = async () => {
        if (currentPageId() !== pageId) return;
        const generation = ++statusGeneration;
        const data = await details(pageId);
        if (generation === statusGeneration) applyDetails(data);
      };
      if (panels.size >= 100) panels.delete(panels.values().next().value);
      panels.add({ root, refresh });
      refreshButton.addEventListener('click', () => refresh().catch(() => showError('Unable to refresh upload status.')));
      refresh().catch(() => showError('Unable to load upload status. Click Refresh upload status to retry.'));
      input.addEventListener('change', () => {
        status.textContent = '';
        const zip = input.files?.[0];
        button.disabled = true;
        retryButton.disabled = true;
        if (!zip) return;
        if (!zip.name.toLowerCase().endsWith('.zip')) { showError('Select an HTML ZIP file.'); return; }
        if (zip.size === 0 || zip.size > 50 * 1024 * 1024) { showError('ZIP must be non-empty and 50 MB or smaller.'); return; }
        updateButtons();
      });
      const performUpload = async (retry) => {
        if (busy || uploads.has(pageId)) return;
        if (!Number.isSafeInteger(pageId) || pageId <= 0) { showError('Save or select a published HTML page first.'); return; }
        if (currentPageId() !== pageId) { showError('Page selection changed. Select HTML Page Handler again.'); return; }
        const zip = input.files?.[0];
        if (!zip || !zip.name.toLowerCase().endsWith('.zip') || zip.size === 0 || zip.size > 50 * 1024 * 1024) {
          showError('Select a non-empty HTML ZIP, 50 MB or smaller.'); return;
        }
        busy = true;
        uploads.add(pageId);
        input.disabled = true;
        button.disabled = true;
        retryButton.disabled = true;
        button.textContent = 'Uploading…';
        status.style.color = '';
        status.textContent = 'Validating and replacing the page files on Linode…';
        try {
          const form = new FormData();
          form.append('zip', zip);
          const response = await httpMiddleware({
            url: `${PAGE_BUILDER_ENDPOINTS.HTML_PAGE_API}/${pageId}/${retry ? 'retry' : 'bundle'}`,
            method: 'POST', data: form, headers: { 'Content-Type': undefined },
            withCredentials: true, timeout: 600000, rejectOnError: true,
          });
          if (!response || response.status !== 200) throw new Error(response?.data?.code || 'ZIP replacement failed.');
          pending.delete(pageId);
          statusGeneration++;
          applyDetails({ ...response.data, retryAllowed: false });
          input.value = '';
          status.textContent = 'ZIP replaced on Linode and published. Refresh the published page to see the new content.';
          showSuccessToast(`HTML page ZIP replaced and published (version ${response.data.version}).`);
        } catch (failure) {
          const code = failure.response?.data?.code;
          showError(htmlPageErrorMessage(code || failure.message) || 'Unable to replace ZIP.');
        } finally {
          uploads.delete(pageId);
          busy = false;
          input.disabled = false;
          button.textContent = 'Reupload ZIP';
          pending.delete(pageId);
          try { await refresh(); } catch (_) { uploadInfo = null; }
          updateButtons();
        }
      };
      button.addEventListener('click', () => performUpload(false));
      retryButton.addEventListener('click', () => performUpload(true));
      return root;
    },
  });
}
